"use client"
import { ChangeLanguageResult } from "@/actions/checkout/change-shipping-destination-or-language"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import CountryPickerAccordion from "@/app/(site)/_components/Organisms/Header/CountryPickerAccordion"
import { Country } from "@/app/(site)/_components/Organisms/Header/CountryPickerWrapper"
import { capitalizeWords, resolveDynamicRedirect } from "@/app/(site)/_utils/misc"
import { CENTRA_LOCALE_SHIPPING_COUNTRY_CODES } from "@/data/centra-locale-shipping-country-code"
import { CountryCode } from "@/data/country-data"
import { SHIPPING_COUNTRIES } from "@/data/shipping-countries"
import {
    isShippingCountryCode,
    SHIPPING_COUNTRY_CODES,
    ShippingCountryCode
} from "@/data/shipping-country-code"
import { CENTRA_LANGUAGE_CODE, LanguageOption } from "@/data/supported-languages"
import { CENTRA_MARKET_CODE } from "@/data/supported-markets"
import { CENTRA_PRICELIST_CODE } from "@/data/supported-pricelists"
import { validateCentraLocale } from "@/i18n.config"
import centraPath from "@/lib/centra-path/centra-path-init"
import { setUserLocaleClient } from "@/lib/cookie/site-cookie/cookieActions.client"
import { isClient } from "@/lib/env-utils/env-utils"
import {
    DEFAULT_USER_COUNTRY_CODE,
    UNIVERSAL_DEFAULT_LANGUAGE
} from "@/lib/siteconfig/constants"
import { removeTrailingSlash } from "@/lib/utils"
import shippingCountryCodeToCountry from "@/maps/shipping-country-code-to-country.ts"
import { getCentraLocaleFromCountryCode } from "@/middleware/helpers"
import { CheckoutAPISelection } from "@/types/centra/checkout-api/selection/get-selection"
import { ResultUtil } from "@/types/generic/Result"

export type ShippingDestination = {
    countryName: string | null
    countryCode: CountryCode | null
}

export const handleShippingAndLanguageSelection = async (
    e: React.MouseEvent<HTMLElement> | null, //when called from a onChange event, e is null
    country: string,
    language: string | null,
    path?: string
) => {
    e?.preventDefault()

    const changeLanguageAndReturnNewPath = async (
        country: string,
        language: string | null
    ): Promise<string> => {
        const formData = new FormData()

        // We need to structure and format the country name to match the expected format in the API
        // Only used in the Checkout
        let value = country
        if (isShippingCountryCode(country, SHIPPING_COUNTRY_CODES)) {
            value =
                shippingCountryCodeToCountry.get(
                    country.toLocaleLowerCase() as ShippingCountryCode
                ) ?? DEFAULT_USER_COUNTRY_CODE.toUpperCase()
        }
        formData.append("countryName", value)

        // Path is used to updated and refresh the centra session cookie (handled in the middleware)
        if (path) {
            //When changing between these paths the path will become undefined when splitting, so we need to handle it differently
            if (path === "fr" || path === "de" || path === "en") {
                formData.append("centraLocale", `/${path}`)
            } else {
                formData.append("centraLocale", `/${path.split("/")[1]}`)
            }
        } else {
            //just a safety fallback
            const extractedCountryCode = getCentraLocaleFromCountryCode(country)
            formData.append("centraLocale", extractedCountryCode)
        }

        // This will be appended if we have a non default (null) language, the default is the first in the languages list.
        if (language) {
            // we don't have us language in the app yet, so we need to fallback to en
            // @todo: remove this once we have us language in the app
            if (language === "us") {
                formData.append("languageCode", UNIVERSAL_DEFAULT_LANGUAGE)
            } else {
                formData.append("languageCode", language)
            }
        }
        if (isClient) {
            const res = await fetch("/api/change-language-or-shipping", {
                method: "POST",
                body: formData
            })

            let response = await res.json()

            if (ResultUtil.isFailure(response.result)) {
                console.info(
                    `Failed to change language/shipping country for, language: ${language}, country: ${country}`
                )
                response = {
                    result: {
                        data: {
                            token: "",
                            metadata: {
                                market: CENTRA_MARKET_CODE.Eu,
                                language: CENTRA_LANGUAGE_CODE.En,
                                currency: CENTRA_PRICELIST_CODE.eur,
                                country: {
                                    code: SHIPPING_COUNTRY_CODES.Germany.toLocaleUpperCase(),
                                    name: capitalizeWords(SHIPPING_COUNTRIES.Germany)
                                }
                            },
                            centraLocale: `/${DEFAULT_USER_COUNTRY_CODE}`
                        }
                    }
                }
            }

            const data = response.result.data
            // COUNTRY
            const checkoutApiSelection: CheckoutAPISelection | undefined =
                data?.checkoutApiSelection
            // END COUNTRY

            // LANGUAGE
            const languageCentraSessionCookie = (data as ChangeLanguageResult)
                ?.extractedCentraSessionCookie
            // END LANGUAGE

            const languageOrCountryExtracted =
                (languageCentraSessionCookie?.metadata?.language ||
                    checkoutApiSelection?.location?.language?.language) ??
                ""

            const countryCodeExtractedFromCentraSessionCookieOrCheckoutApiSelection =
                ((languageCentraSessionCookie?.metadata?.country
                    ?.code as ShippingCountryCode) ||
                    (checkoutApiSelection?.location?.country?.toLowerCase() as ShippingCountryCode)) ??
                ""

            const hasShippingCountryEuroCurrency =
                CENTRA_LOCALE_SHIPPING_COUNTRY_CODES[
                    countryCodeExtractedFromCentraSessionCookieOrCheckoutApiSelection.toLowerCase() as ShippingCountryCode
                ]?.length === 0

            const currentPath = window.location.pathname

            const currentMarketLocale = centraPath.getCentraLocaleFromUrl(
                window.location.href
            )

            const centraLocale = hasShippingCountryEuroCurrency
                ? `${languageOrCountryExtracted}`
                : `${languageOrCountryExtracted}-${centraPath.getCentraLocaleShippingCountryFromShippingCountryCode(
                      countryCodeExtractedFromCentraSessionCookieOrCheckoutApiSelection.toLowerCase() as ShippingCountryCode,
                      CENTRA_LOCALE_SHIPPING_COUNTRY_CODES
                  )}`

            const nonLocalePath = currentPath.replace(
                new RegExp(`^/${currentMarketLocale}`),
                ""
            )

            const newPath = removeTrailingSlash(`/${centraLocale}${nonLocalePath}`)

            // Just to be extra sure we fallback to a default path if the newPath is empty/weird
            return newPath.startsWith("/-")
                ? newPath.substring(2)
                : newPath || `/${UNIVERSAL_DEFAULT_LANGUAGE}`
        }
        return ""
    }
    return await changeLanguageAndReturnNewPath(country, language)
}

export default function CountryPickerSelectionItems({
    countriesWithLanguages,
    countries,
    theme = "dark",
    extractedAbsolutePath
}: {
    theme?: "light" | "dark"
    countries: Country[]
    countriesWithLanguages: Record<ShippingCountryCode, LanguageOption[]>
    extractedAbsolutePath?: string | null
}) {
    const siteUrl = process.env.NEXT_PUBLIC_SITEMAP_DOMAIN || "https://localhost:3000"
    const handleCallback = async (
        path: string = `/${DEFAULT_USER_COUNTRY_CODE}`,
        countryCode: string
    ) => {
        if (!validateCentraLocale(path.split("/")[1])) {
            path = `/${UNIVERSAL_DEFAULT_LANGUAGE}`
        }
        setUserLocaleClient({ path, countryCode })
    }

    const buildHrefForLocale = (locale: string): string => {
        if (!extractedAbsolutePath) return `/${locale.replace(/^\/+/, "")}`
        const pathWithLocale = `/${locale.replace(/^\/+/, "")}/${extractedAbsolutePath}`
        const redirectedPath = resolveDynamicRedirect(pathWithLocale)
        return redirectedPath
    }

    return countries.map(country => {
        const languages =
            countriesWithLanguages[country.countryCode as ShippingCountryCode] || []

        return (
            <li
                key={country.countryCode}
                className="mb-9 flex flex-col border-b border-solid border-main-dark-grey" // mb-8 +1 for the divider that is inside the CountryPickerAccordion
            >
                {languages.length > 1 ? (
                    <CountryPickerAccordion
                        label={country.value}
                        elementId={country.countryCode}
                    >
                        {languages.map(({ value, path, label }) => {
                            const buildAbsolutePath = buildHrefForLocale(
                                path || `/${UNIVERSAL_DEFAULT_LANGUAGE}`
                            )
                            return (
                                <a
                                    key={value}
                                    href={`${siteUrl}${buildAbsolutePath}` || "#"}
                                    className={`leading-160 group flex items-center py-6 pl-16 font-trimRegular text-16 font-400 uppercase -tracking-[0.02em]`}
                                    onClick={async e => {
                                        const newPath =
                                            await handleShippingAndLanguageSelection(
                                                e,
                                                country.value,
                                                value,
                                                path
                                            )
                                        await handleCallback(path, country.countryCode)
                                        if (isClient) {
                                            if (newPath === window.location.pathname) {
                                                location.reload()
                                            } else {
                                                window.location.href = newPath
                                            }
                                        }
                                    }}
                                >
                                    <span className="md:transition-colors md:duration-300 md:group-hover:text-shades-80-white">
                                        {label}
                                    </span>
                                    <IconArrowRight className="ml-2 h-12 w-12 opacity-0 group-hover:text-shades-80-white md:h-16 md:w-16 md:translate-x-[-10px] md:transition-transform md:duration-300 md:group-hover:translate-x-0 md:group-hover:opacity-100" />
                                </a>
                            )
                        })}
                    </CountryPickerAccordion>
                ) : (
                    (() => {
                        const fallbackLocale =
                            languages[0]?.path || `/${UNIVERSAL_DEFAULT_LANGUAGE}`
                        const absoluteHref = buildHrefForLocale(fallbackLocale)
                        return (
                            <a
                                href={`${siteUrl}${absoluteHref}` || "#"}
                                className={`group ${theme === "light" ? "text-black" : "text-white"} leading-160 flex items-center gap-5 pb-8 font-trimRegular text-16 font-400 uppercase -tracking-[0.02em] md:transition-colors md:duration-300 md:hover:text-shades-80-white`}
                                onClick={async e => {
                                    const newPath =
                                        await handleShippingAndLanguageSelection(
                                            e,
                                            country.value,
                                            null,
                                            languages[0]?.path
                                        )
                                    handleCallback(
                                        languages[0]?.path,
                                        country.countryCode
                                    )
                                    if (isClient) {
                                        if (newPath === window.location.pathname) {
                                            location.reload()
                                        } else {
                                            window.location.href = newPath
                                        }
                                    }
                                }}
                            >
                                <span className="md:transition-colors md:duration-300 md:group-hover:text-shades-80-white">
                                    {country.value}
                                </span>
                                <IconArrowRight className="ml-2 h-12 w-12 opacity-0 group-hover:text-shades-80-white md:h-16 md:w-16 md:translate-x-[-10px] md:transition-transform md:duration-300 md:group-hover:translate-x-0 md:group-hover:opacity-100" />
                            </a>
                        )
                    })()
                )}
            </li>
        )
    })
}
